import React from "react"
import PropTypes from "prop-types"

// conditionally render classNames
import classNames from "classnames"

// style for all Inlines
import "app/static/stylus/LongInlinePresentation.styl"

import { Card } from "QuorumDesign"

// components for all Inlines
import Expand from "app/static/frontend/search-new/components/Inline/components/ExpandControl"
import Options from "app/static/frontend/search-new/components/Inline/components/InlineOptions"
import Snippets from "app/static/frontend/search-new/components/Inline/components/Snippets"
import Tooltip from "components/Tooltip"

// components for FeedItems
import InlineFeedItem from "app/static/frontend/search-new/components/Inline/components/FeedItem"

// label components for FeedItem, Note, and Vote Inlines
import InlineCustomFields from "app/static/frontend/search-new/components/Inline/labels/InlineCustomFields"
import InlineBills from "app/static/frontend/search-new/components/Inline/labels/InlineBills"

// components for notes
import AttachmentViewer from "app/static/frontend/components/AttachmentViewer"
import { SegueLink } from "app/static/frontend/middleware/segueMiddleware"

// component for Documents of type constituent_email
import QuorumIframe from "app/static/frontend/components/QuorumIframe"

// components for bills
import BillStatusTimeline from "app/static/frontend/components/BillStatusTimeline"
import RegulationStatusTimeline from "app/static/frontend/components/RegulationStatusTimeline"

// components for documents, files, regulations
import PDFViewer from "app/static/frontend/components/PDFViewer"

// components for bills, documents, votes, amendments, webpages, and regulations
import ListAdder from "app/static/frontend/projects-new/new-list/NewListAdder"

import IssueAdder from "app/static/frontend/components/IssueAdder"

// component for regcomments
import AttachmentsPdfSelectViewer from "app/static/frontend/components/AttachmentsPdfSelectViewer"

// TODO: what is this used for?
// where is it used?
import { DesignSystemDataTableView } from "app/static/frontend/profiles-new/components/DataTableView/index"

// striptags from Tooltip onHover events
import striptags from "striptags"

// Inline helper functions
import {
    checkObjectIsEmpty,
    getInlineAnchorPath,
    handleClickOutside,
    onError,
    renderText,
    segueToProfile,
} from "app/static/frontend/search-new/components/Inline/shared/functions"

// helpers
import { isArabic } from "shared/search/helperFunctions"
import { stringContainsHTMLMarkup } from "shared/imports/sharedHelperFunctions"
import { NOTE_CUSTOM_FIELDS_TRUNCATION_LIMIT } from "shared/search/constants"

import { mobileBreakpoint } from "app/static/frontend/design-constants"
import { MobileCard } from "app/static/frontend/search-new/components/Inline/MobileCard"

const LongInlinePresentation = (props) => {
    React.useEffect(() => {
        // for single note view, we want to expand the card
        if (
            props.quorumDataType?.value === DjangIO.app.models.QuorumDataType.note.value &&
            window.location.search.includes("?id__in=")
        ) {
            props.updateInlineAppearance(props.uniqueFlvId, props.inlineId, {
                expanded: true,
            })
        }
    }, [])

    const isNotClickable = props.noResponse || props.hasNoProfile

    /** helper functions */
    const handleOutside = () => handleClickOutside(props.updateInlineAppearance, props.uniqueFlvId, props.inlineId)

    /** conditionally render the Note Inline attachment */
    const renderAttachments = () => (
        <AttachmentViewer
            attachments={
                props.quorumDataType?.value === DjangIO.app.models.QuorumDataType.note.value &&
                !props.appearanceValues.get("expanded")
                    ? props.attachments.slice(0, NOTE_CUSTOM_FIELDS_TRUNCATION_LIMIT)
                    : props.attachments
            }
            hideDelete
            showEllipsis={
                props.quorumDataType?.value === DjangIO.app.models.QuorumDataType.note.value &&
                !props.appearanceValues.get("expanded") &&
                props.attachments &&
                props.attachments.length > 4
            }
        />
    )

    /** If a Note has an associated Bill, render it */
    const renderBills = () => <InlineBills bill={props.bill} bills={props.bills} inlineId={props.inlineId} />

    /** conditionally render the Bill Inline timeline */
    const renderBillTimeline = () => (
        <BillStatusTimeline
            currentGeneralStatus={props.currentGeneralStatus}
            currentGeneralStatusEnum={props.currentGeneralStatusEnum}
        />
    )

    const renderRegulationTimeline = () => (
        <RegulationStatusTimeline achievedStatuses={props.achievedStatuses} timelineEnum={props.timelineEnum} />
    )

    const renderCustomFields = () => <InlineCustomFields customFields={props.customFields} />

    const renderFeedItem = () => (
        <InlineFeedItem
            bigCache={props.feedItemCache}
            inlineId={props.inlineId}
            isSearchify={props.isHighlightable}
            quorumDataType={props.quorumDataType}
        />
    )

    /** if the Inline requires a "See More" dropdown button, render it */
    const renderExpandControl = () => (
        <Expand
            appearanceValues={props.appearanceValues}
            inlineId={props.inlineId}
            hide={props.hideExpand}
            onClick={props.expandOnClick}
            pdfUrl={props.pdfUrl}
            uniqueFlvId={props.uniqueFlvId}
            updateInlineAppearance={props.updateInlineAppearance}
        />
    )

    /** if our Inline has option buttons (download, delete, edit), render them in the bottom right of the Inline */
    const renderOptions = () =>
        props.options &&
        !props.isHighlightable && <Options isSelected={props.isSearchifySelected} options={props.options} />

    /** render the text data into the Inline */
    const renderInnerFirstLine = () => (
        <h2
            id={props.titleId}
            className={classNames("first-line", { "hoverscroll title-hoverscroll": props.isSmallIcon })}
        >
            {
                // Bill, Document, Event, Committee and Caucus Inlines will have a region prepended to the title
                props.showRegion && props.region && (
                    <span className="region">
                        {props.region}
                        {!props.disableRegionColon ? `: ` : ``}
                    </span>
                )
            }
            {
                // Event (sometimes) and Note (always) Inlines contain backend-generated html
                //      that must be recursively parsed into React elements
                renderText({
                    lineData:
                        props.firstLineFullData && props.appearanceValues.get("expanded") && props.firstLineFullData,
                    isHtml: props.firstLineIsHtml,
                    inlineId: props.inlineId,
                    isSearchify: props.isHighlightable,
                })
            }
        </h2>
    )

    /** primary Inline render methods */
    const renderFirstLine = () =>
        // only show the Tooltip Component if the text overflowed out of the firstLine <h2> box
        props.appearanceValues.get("shouldShowTooltip") ? (
            <Tooltip
                label={props.rawContent || props.firstLineIsHtml ? striptags(props.firstLineData) : props.firstLineData}
            >
                {renderInnerFirstLine()}
            </Tooltip>
        ) : (
            renderInnerFirstLine()
        )

    const renderInnerSecondLine = () => (
        <h3
            id={props.bylineId}
            className={
                // we only want to pad this space if we are rendering the "See More" button
                props.appearanceValues.get("hasExpandControl")
                    ? classNames({
                          seeMore: props.appearanceValues.get("hasExpandControl"),
                      })
                    : // this ensures that we do not pass a blank "class" attribute down to the rendered DOM
                      undefined
            }
        >
            {props.enactedText && (
                <span className={classNames({ enacted: props.hasBeenEnacted })}>{props.enactedText}</span>
            )}
            {props.enactedText && props.secondLineData && " | "}
            {props.secondLineData && (
                <span>
                    {renderText({
                        lineData: props.secondLineData,
                        isHtml: props.secondLineIsHtml,
                        inlineId: props.inlineId,
                        isSearchify: props.isHighlightable,
                    })}
                </span>
            )}
            {props.squashThirdLine && props.thirdLineData && " | "}
            {props.squashThirdLine &&
                props.thirdLineData &&
                renderText({
                    lineData: props.thirdLineData,
                    isHtml: props.thirdLineIsHtml,
                    inlineId: props.inlineId,
                    isSearchify: props.isHighlightable,
                })}
        </h3>
    )

    const renderSecondLineTooltip = () => {
        const label = []

        if (props.enactedText) {
            label.push(props.enactedText)
        }

        if (props.secondLineData) {
            const secondLineData = props.secondLineIsHtml ? striptags(props.secondLineData) : props.secondLineData
            label.push(secondLineData)
        }

        if (props.squashThirdLine && props.thirdLineData) {
            const thirdLineData = props.thirdLineIsHtml ? striptags(props.thirdLineData) : props.thirdLineData
            label.push(thirdLineData)
        }

        return <Tooltip label={label.join(" | ")}>{renderInnerSecondLine()}</Tooltip>
    }

    const renderSecondLine = () =>
        // only show the Tooltip Component if the text overflowed out of the secondLine <h3> box
        props.appearanceValues.get("shouldShowBylineTooltip") ? renderSecondLineTooltip() : renderInnerSecondLine()

    const renderThirdLine = () => {
        const hasAttachments = props.attachments && props.attachments.length
        const hasSnippets = props.snippets && props.snippets.length
        const { alertSnippets } = props
        const hasAlertSnippets = alertSnippets && checkObjectIsEmpty(alertSnippets)

        if (
            (props.thirdLineData ||
                props.pdfUrl ||
                props.expandedIFrameUrl ||
                hasAttachments ||
                hasSnippets ||
                hasAlertSnippets) &&
            (!props.squashThirdLine || hasSnippets || hasAlertSnippets)
        ) {
            return (
                <div className={classNames("third-line", props.customThirdLineClass)}>
                    {(props.thirdLineData ||
                        props.pdfUrl ||
                        props.expandedIFrameUrl ||
                        props.snippets ||
                        props.alertSnippets) &&
                        // ternary required due to "Can only set one of `children` or `props.dangerouslySetInnerHTML`" error
                        (!props.dangerouslyInsertThirdLine ? (
                            <div
                                // manually set the unique id of the description element since this is a stateless function
                                // we will grab the vanilla DOM node in the parent Component"s componentDidMount function
                                id={props.descriptionId}
                                className={classNames("description", {
                                    expanded: props.alwaysExpanded || props.appearanceValues.get("expanded"),
                                })}
                                dir={isArabic(props.thirdLineData) ? "rtl" : "ltr"}
                            >
                                {
                                    // Bills, FeedItems, and Webpages return links to snippet arrays on advanced search
                                    props.snippets && (
                                        <Snippets
                                            inlineId={props.inlineId}
                                            isExpanded={props.appearanceValues.get("expanded")}
                                            isSearchify={props.isHighlightable}
                                            snippets={props.snippets}
                                        />
                                    )
                                }
                                {
                                    // only show the thirdLine text if
                                    //      - we have not expanded the Inline,
                                    //      - it does not contain a PDF,
                                    //      - and no snippets were returned
                                    !(
                                        (props.pdfUrl && props.appearanceValues.get("expanded")) ||
                                        (props.expandedIFrameUrl && props.appearanceValues.get("expanded")) ||
                                        props.alertSnippets ||
                                        props.squashThirdLine
                                    ) &&
                                        // Note Inlines contain html that must be recursively parsed into react elements
                                        renderText({
                                            lineData: props.appearanceValues.get("expanded")
                                                ? props.thirdLineData
                                                : !props.hideAllThirdLineData &&
                                                  props.thirdLineData &&
                                                  props.thirdLineData.substring(0, 2000),
                                            isHtml: props.thirdLineIsHtml,
                                            inlineId: props.inlineId,
                                            isSearchify: props.isHighlightable,
                                        })
                                }
                                {
                                    // only show external PDFs if we pass the PDF URL
                                    // (some Document Inlines contain links to external PDFs)
                                    props.pdfUrl && props.appearanceValues.get("expanded") && (
                                        <PDFViewer
                                            className="pdf"
                                            onClick={(e) => e.stopPropagation()}
                                            url={props.pdfUrl}
                                            key={props.pdfUrl}
                                        />
                                    )
                                }
                                {
                                    // Some Article Inlines contain reference to external webpages.
                                    // Only show them if we pass the iframe URL.
                                    props.expandedIFrameUrl && props.appearanceValues.get("expanded") && (
                                        <div>
                                            <iframe
                                                width="100%"
                                                height="600"
                                                src={props.expandedIFrameUrl}
                                                style={{ border: "none" }}
                                                target="webDisplay"
                                                title={props.expandedIFrameUrl}
                                            />
                                        </div>
                                    )
                                }
                                {props.alertSnippets && (
                                    <Snippets
                                        alertSnippets={props.alertSnippets}
                                        inlineId={props.inlineId}
                                        isExpanded={props.appearanceValues.get("expanded")}
                                        isSearchify={props.isHighlightable}
                                    />
                                )}
                            </div>
                        ) : (
                            <div
                                id={props.descriptionId}
                                className={classNames("description", "external", {
                                    expanded: props.appearanceValues.get("expanded"),
                                })}
                                // we can"t relaibly set the onClick attribute for embedded anchor elements,
                                //      so we set it on the parent
                                onClick={(e) => e.stopPropagation()}
                                onKeyDown={(e) => e.stopPropagation()}
                                role="button"
                                tabIndex="0"
                                // this is required for:
                                //      Document "Newsletters to Constituents"
                                //      Events that contain invalid html
                                // we can also use the <object> element with type="text/html" and "data="rather than "src="
                                // to also syntactically denote external content being inserted and sandboxed into the page
                                // (we chose iframe for now since it has better support for legacy browsers)
                            >
                                <QuorumIframe htmlContent={props.thirdLineData} id={props.inlineId} />
                            </div>
                        ))}
                    {props.attachments && props.attachments.length > 0 && renderAttachments()}
                    {props.regCommentPdfAttachments &&
                        props.regCommentPdfAttachments.length > 0 &&
                        props.appearanceValues.get("expanded") && (
                            <AttachmentsPdfSelectViewer regCommentPdfAttachments={props.regCommentPdfAttachments} />
                        )}
                </div>
            )
        }

        return undefined
    }

    const renderFourthLine = () => (
        <div className="description">
            {renderText({
                lineData: props.fourthLineData,
                isHtml: props.fourthLineIsHtml,
                inlineId: props.inlineId,
                isSearchify: props.isHighlightable,
            })}
        </div>
    )

    const renderDataTableView = () => <DesignSystemDataTableView data={props.dataTableViewData} hideTitle />

    /** if the Inline itself should be rendered, render it */
    if (!props.appearanceValues.get("isHidden")) {
        // the id of either the inline or underlying object keyed to a FeedItem
        // this is used to make the InlineListAdder, PriorityAdder, StanceAdder, and IssueAdder components work
        // for all inlines (including FeedItem inlines, which contain data about their underlying object)
        const adderId =
            // FeedItems
            props.underlyingItemId ||
            // everything else
            props.inlineId

        const renderAdder = () => {
            if (props.appearanceValues.get("listDropDownEnabled")) {
                return (
                    <ListAdder
                        inline
                        showHeader
                        datumId={adderId}
                        handleClickOutside={handleOutside}
                        model={props.model}
                    />
                )
            } else if (props.appearanceValues.get("issueDropDownEnabled")) {
                return (
                    <IssueAdder
                        inline
                        dataType={DjangIO.app.models.QuorumDataType.by_value(props.model)}
                        profileId={adderId}
                        handleClickOutside={handleOutside}
                    />
                )
            }

            return undefined
        }

        const getAdderType = () => {
            if (props.appearanceValues.get("listDropDownEnabled")) {
                return "Lists"
            } else if (props.appearanceValues.get("issueDropDownEnabled")) {
                return "Issues"
            }

            return undefined
        }

        const isMobile = window.innerWidth <= mobileBreakpoint

        const CardComponent = isMobile ? MobileCard : Card

        const renderSecondLineDS = () => {
            const secondLineDSToRender =
                props.secondLineFullDS && props.appearanceValues.get("expanded")
                    ? props.secondLineFullDS
                    : props.secondLineDS
            return (
                props.secondLineDS &&
                (props.secondLineDSIsHtml
                    ? secondLineDSToRender.map((secondLineItem) =>
                          stringContainsHTMLMarkup(secondLineItem.value)
                              ? {
                                    ...secondLineItem,
                                    value: renderText({
                                        lineData: secondLineItem.value,
                                        isHtml: true,
                                        inlineId: props.inlineId,
                                        isSearchify: props.isHighlightable,
                                    }),
                                }
                              : secondLineItem,
                      )
                    : secondLineDSToRender)
            )
        }

        return (
            <CardComponent
                AdderComponent={renderAdder()}
                adderType={getAdderType()}
                // S.Card .shared-inline
                cardClassName={classNames("shared-inline", { "half-width": props.isHalfWidth })}
                cardDataCy={`shared-inline-${props.index}`}
                // S.CardInner .inline
                cardInnerClassName={classNames(
                    "inline",
                    // if the prop does not have a profile page, reset the cursor to its default value
                    { "inline-nosegue": isNotClickable },
                    { "is-searchify": props.isHighlightable },
                    {
                        "is-searchify-selected":
                            props.isSearchifySelected || (props.isBulkActions && props.isBulkActionsSelected),
                    },
                )}
                cardInnerOnClick={(event) =>
                    (!props.hasNoProfile || props.isHighlightable) && segueToProfile(props.onPress, event)
                }
                // a
                cardInnerAnchorHref={
                    // if the Inline can segue, then we also want to be able to right click and open it in a new tab
                    getInlineAnchorPath({
                        isHighlightable: props.isHighlightable,
                        href: props.href,
                        inlineId: props.inlineId,
                    })
                }
                // Icon
                imageUrlPath={props.imageUrlPath}
                imagePath={props.imagePath}
                imagePathClassName={classNames("person-profile-img", "url-image", props.imageBorderRule)}
                imagePathOnError={onError}
                imageStyle={props.imageStyle}
                stateIcon={props.stateIcon}
                abbrev={props.abbrev}
                iconName={props.iconName}
                iconNameContainerClassName={classNames(props.voteResult, { enacted: props.hasBeenEnacted })}
                iconNameClassName={classNames(
                    `inline-icon fa fa-fw fa-${props.iconName}`,
                    props.voteResult,
                    { "half-width-icon": props.isSmallIcon },
                    // required due to wide width of "fa-handshake-o" icon in Notes FLV
                    {
                        "inline-icon-shrink":
                            props.iconName ===
                            DjangIO.app.grassroots.enums.ActionItemType.interaction.icon.replace("fa-", ""),
                    },
                )}
                fa5IconName={props.fa5IconName}
                fa5IconFamily={props.fa5IconFamily}
                // .inline-text
                inlineTextChildren={
                    <React.Fragment>
                        {!props.secondLineDS && (props.secondLineData || props.enactedText) && renderSecondLine()}
                    </React.Fragment>
                }
                // firstLine
                firstLineRegion={
                    // Bill, Document, Event, Committee and Caucus Inlines will have a region prepended to the title
                    props.showRegion && props.region && `${props.region}${!props.disableRegionColon ? `: ` : ``}`
                }
                firstLineId={props.titleId}
                firstLineText={
                    (props.firstLineData || props.showRegion) &&
                    renderText({
                        lineData:
                            props.appearanceValues.get("expanded") && props.firstLineFullData
                                ? props.firstLineFullData
                                : props.firstLineData,
                        isHtml: props.firstLineIsHtml,
                        inlineId: props.inlineId,
                        isSearchify: props.isHighlightable,
                    })
                }
                // feedItem
                // TODO components, style
                feedItem={
                    props.feedItemCache && (
                        <InlineFeedItem
                            bigCache={props.feedItemCache}
                            inlineId={props.inlineId}
                            isSearchify={props.isHighlightable}
                            quorumDataType={props.quorumDataType}
                        />
                    )
                }
                // thirdLine
                // TODO style
                shouldRenderThirdLine={
                    (props.thirdLineData ||
                        props.pdfUrl ||
                        props.expandedIFrameUrl ||
                        (props.attachments && props.attachments.length) ||
                        (props.snippets && props.snippets.length) ||
                        (props.alertSnippets && checkObjectIsEmpty(props.alertSnippets))) &&
                    (!props.squashThirdLine ||
                        (props.snippets && props.snippets.length) ||
                        (props.alertSnippets && checkObjectIsEmpty(props.alertSnippets)))
                }
                thirdLineClassName={classNames("third-line", props.customThirdLineClass)}
                shouldRenderThirdLineText={
                    props.thirdLineData ||
                    props.pdfUrl ||
                    props.expandedIFrameUrl ||
                    props.snippets ||
                    props.alertSnippets
                }
                dangerouslyInsertThirdLine={props.dangerouslyInsertThirdLine}
                thirdLineTextId={props.descriptionId}
                thirdLineTextClassName={classNames("description", {
                    expanded: props.alwaysExpanded || props.appearanceValues.get("expanded"),
                })}
                thirdLineTextDir={isArabic(props.thirdLineData) ? "rtl" : "ltr"}
                thirdLineTextSnippets={
                    props.snippets && (
                        <Snippets
                            // FeedItem
                            alertSnippets={props.alertSnippets}
                            inlineId={props.inlineId}
                            isExpanded={props.appearanceValues.get("expanded")}
                            isSearchify={props.isHighlightable}
                            // Article, Bill, Regulation
                            snippets={props.snippets}
                        />
                    )
                }
                thirdLineText={
                    !(
                        (props.pdfUrl && props.appearanceValues.get("expanded")) ||
                        (props.expandedIFrameUrl && props.appearanceValues.get("expanded")) ||
                        props.alertSnippets ||
                        props.squashThirdLine
                    ) &&
                    renderText({
                        lineData: props.appearanceValues.get("expanded")
                            ? props.thirdLineData
                            : !props.hideAllThirdLineData &&
                              props.thirdLineData &&
                              props.thirdLineData.substring(0, 2000),
                        isHtml: props.thirdLineIsHtml,
                        inlineId: props.inlineId,
                        isSearchify: props.isHighlightable,
                    })
                }
                thirdLinePdf={
                    props.pdfUrl &&
                    props.appearanceValues.get("expanded") && (
                        <PDFViewer
                            className="pdf"
                            onClick={(event) => event.stopPropagation()}
                            url={props.pdfUrl}
                            key={props.pdfUrl}
                        />
                    )
                }
                thirdLineIframe={
                    // Some Article Inlines contain reference to external webpages.
                    // Only show them if we pass the iframe URL.
                    props.expandedIFrameUrl &&
                    props.appearanceValues.get("expanded") && (
                        <div>
                            <iframe
                                width="100%"
                                height="600"
                                src={props.expandedIFrameUrl}
                                style={{ border: "none" }}
                                target="webDisplay"
                                title={props.expandedIFrameUrl}
                            />
                        </div>
                    )
                }
                thirdLineQuorumIframeClassName={classNames("description", "external", {
                    expanded: props.appearanceValues.get("expanded"),
                })}
                thirdLineQuorumIframeOnClick={(event) => event.stopPropagation()}
                thirdLineQuorumIframe={<QuorumIframe htmlContent={props.thirdLineData} id={props.inlineId} />}
                thirdLineAttachments={
                    props.attachments &&
                    props.attachments.length > 0 && <AttachmentViewer attachments={props.attachments} hideDelete />
                }
                thirdLineAttachmentsPdfSelectViewer={
                    props.regCommentPdfAttachments &&
                    props.regCommentPdfAttachments.length > 0 &&
                    props.appearanceValues.get("expanded") && (
                        <AttachmentsPdfSelectViewer regCommentPdfAttachments={props.regCommentPdfAttachments} />
                    )
                }
                // fourthLine
                // TODO style
                fourthLineText={
                    props.fourthLineData &&
                    (!props.showFourthLineIfExpanded ||
                        (props.showFourthLineIfExpanded && props.appearanceValues.get("expanded"))) &&
                    renderText({
                        lineData: props.fourthLineData,
                        isHtml: props.fourthLineIsHtml,
                        inlineId: props.inlineId,
                        isSearchify: props.isHighlightable,
                    })
                }
                // DesignSystemDataTableView
                DesignSystemDataTableView={
                    props.dataTableViewData &&
                    props.appearanceValues.get("expanded") && (
                        <DesignSystemDataTableView data={props.dataTableViewData} hideTitle />
                    )
                }
                // secondLineDesignSystem
                secondLineDS={renderSecondLineDS()}
                customFields={
                    props.quorumDataType?.value === DjangIO.app.models.QuorumDataType.note.value &&
                    !props.appearanceValues.get("expanded") &&
                    props.customFields.slice(0, NOTE_CUSTOM_FIELDS_TRUNCATION_LIMIT)
                }
                secondLineShowEllipsis={
                    !props.appearanceValues.get("expanded") &&
                    props.secondLineFullDS &&
                    (props.secondLineFullDS.length > props.secondLineDS.length || props.customFields.length > 4)
                }
                SegueLinkComponent={SegueLink}
                // Bill, Regulation
                timelineStatuses={props.timelineStatuses}
                timelineEnum={props.timelineEnum}
                // Options
                isHighlightable={props.isHighlightable}
                isSelected={props.isSearchifySelected || (props.isBulkActions && props.isBulkActionsSelected)}
                options={props.options}
                hasSeeMore={props.hideAllThirdLineData || props.appearanceValues.get("hasExpandControl")}
                seeMoreEnabled={props.appearanceValues.get("expanded")}
                seeMoreIcon={props.appearanceValues.get("expanded") ? "angle-up" : "angle-down"}
                seeMoreIconFamily="fal"
                seeMoreText={props.appearanceValues.get("expanded") ? "Show Less" : "See More"}
                seeMoreHref={props.pdfUrl}
                seeMoreOnClick={(event) => {
                    // prevent href and Segue from firing, respectively
                    event.preventDefault()
                    event.stopPropagation()

                    if (props.expandOnClick && !props.appearanceValues.get("expanded")) {
                        props.expandOnClick()
                    }

                    return props.updateInlineAppearance(props.uniqueFlvId, props.inlineId, {
                        expanded: !props.appearanceValues.get("expanded"),
                    })
                }}
                rightSection={props.rightSection}
            />
        )
    }

    // if we have hidden or deleted the Inline, we want to prevent react from re-rendering it to the page
    // https://stackoverflow.com/questions/42083181/is-it-possible-to-return-empty-in-react-render-function
    return null
}

LongInlinePresentation.propTypes = {
    abbrev: PropTypes.string,
    achievedStatuses: PropTypes.object,
    alertSnippets: PropTypes.object,
    alwaysExpanded: PropTypes.bool,
    appearanceValues: PropTypes.object,
    attachments: PropTypes.array,
    bill: PropTypes.object,
    bills: PropTypes.arrayOf(PropTypes.object),
    bylineId: PropTypes.string,
    currentGeneralStatus: PropTypes.number,
    currentGeneralStatusEnum: PropTypes.array,
    customFields: PropTypes.arrayOf(PropTypes.object),
    customThirdLineClass: PropTypes.string,
    dangerouslyInsertThirdLine: PropTypes.bool,
    descriptionId: PropTypes.string,
    disableRegionColon: PropTypes.bool,
    enactedText: PropTypes.string,
    expandedIFrameUrl: PropTypes.string,
    fa5IconFamily: PropTypes.string,
    fa5IconName: PropTypes.string,
    feedItemCache: PropTypes.object,
    firstLineData: PropTypes.string,
    firstLineIsHtml: PropTypes.bool,
    fourthLineData: PropTypes.string,
    fourthLineIsHtml: PropTypes.bool,
    hasBeenEnacted: PropTypes.bool,
    hasNoProfile: PropTypes.bool,
    hideExpand: PropTypes.bool,
    href: PropTypes.string,
    iconName: PropTypes.string,
    imageBorderRule: PropTypes.string,
    imagePath: PropTypes.string,
    imageStyle: PropTypes.object,
    imageUrlPath: PropTypes.string,
    index: PropTypes.number,
    inlineId: PropTypes.number,
    isHalfWidth: PropTypes.bool,
    isBulkActions: PropTypes.bool,
    isBulkActionsSelected: PropTypes.bool,
    isHighlightable: PropTypes.bool,
    isSearchify: PropTypes.bool,
    isSearchifySelected: PropTypes.bool,
    isSmallIcon: PropTypes.bool,
    model: PropTypes.number,
    noResponse: PropTypes.bool,
    onPress: PropTypes.func,
    options: PropTypes.arrayOf(PropTypes.object),
    pdfUrl: PropTypes.string,
    quorumDataType: PropTypes.object,
    rawContent: PropTypes.string,
    regCommentPdfAttachments: PropTypes.arrayOf(PropTypes.object),
    region: PropTypes.string,
    renderBills: PropTypes.bool,
    renderCustomFields: PropTypes.bool,
    secondLineData: PropTypes.string,
    secondLineIsHtml: PropTypes.bool,
    showFourthLineIfExpanded: PropTypes.bool,
    showRegion: PropTypes.bool,
    snippets: PropTypes.arrayOf(PropTypes.string),
    squashThirdLine: PropTypes.bool,
    stateIcon: PropTypes.string,
    timelineEnum: PropTypes.object,
    titleId: PropTypes.string,
    thirdLineData: PropTypes.string,
    thirdLineIsHtml: PropTypes.bool,
    underlyingItemId: PropTypes.number,
    uniqueFlvId: PropTypes.string,
    updateInlineAppearance: PropTypes.func,
    voteResult: PropTypes.string,
    dataTableViewData: PropTypes.array,
}

export default LongInlinePresentation
